import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { matrimonyApi } from '../matrimonyApi';

const EDUCATIONS = ['Any', 'High School', 'Diploma', 'Bachelor\'s', 'Master\'s', 'PhD', 'Professional Degree'];

function initials(name) {
  return (name || 'AB').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function ProfileCard({ p }) {
  return (
    <div className="m-profile-card">
      <div className="m-profile-card-header">
        <div className="m-profile-card-img">
          {p.profile_photo
            ? <img src={p.profile_photo} alt={p.full_name} />
            : initials(p.full_name)}
        </div>
        <div className="m-profile-card-name">{p.full_name || 'Anonymous'}</div>
        <div className="m-profile-card-meta">
          {p.age && <span>{p.age} yrs</span>}
          {p.city && <><span>·</span><span>{p.city}{p.state ? `, ${p.state}` : ''}</span></>}
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', justifyContent: 'center' }}>
          {p.gender && (
            <span className={`m-badge m-badge-${p.gender}`}>{p.gender === 'bride' ? '👰 Bride' : '🤵 Groom'}</span>
          )}
          {p.religion && <span className="m-badge m-badge-gold">{p.religion}</span>}
        </div>
      </div>
      <div className="m-profile-card-body">
        {p.caste && (
          <div className="m-profile-card-row">
            <span className="m-profile-card-row-icon">🙏</span>
            <span>{p.caste}</span>
          </div>
        )}
        {p.education && (
          <div className="m-profile-card-row">
            <span className="m-profile-card-row-icon">📚</span>
            <span>{p.education}{p.education_field ? ` — ${p.education_field}` : ''}</span>
          </div>
        )}
        {p.occupation && (
          <div className="m-profile-card-row">
            <span className="m-profile-card-row-icon">💼</span>
            <span>{p.occupation}</span>
          </div>
        )}
        {p.about_me && <p className="m-profile-card-about">{p.about_me}</p>}
      </div>
      <div className="m-profile-card-footer">
        <Link to={`/profiles/${p.id}`} className="m-btn m-btn-primary m-btn-sm" style={{ flex: 1, textAlign: 'center' }}>
          View Profile 💍
        </Link>
      </div>
    </div>
  );
}

export default function MatrimonyProfiles() {
  const location = useLocation();
  const qs = new URLSearchParams(location.search);

  const [filters, setFilters] = useState({
    gender:   qs.get('gender')   || '',
    ageMin:   qs.get('ageMin')   || '',
    ageMax:   qs.get('ageMax')   || '',
    religion: qs.get('religion') || '',
    state:    qs.get('state')    || '',
    education: qs.get('education') || '',
    search:   qs.get('search')   || '',
  });

  const [profiles, setProfiles] = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(0);
  const [loading, setLoading]   = useState(true);
  const [filterOptions, setFilterOptions] = useState({ religions: [], states: [] });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    matrimonyApi.getFilters().then(setFilterOptions).catch(() => {});
  }, []);

  const loadProfiles = useCallback((f, pg, append = false) => {
    setLoading(true);
    const params = { ...f, page: pg, size: 16 };
    matrimonyApi.getProfiles(params)
      .then(res => {
        setProfiles(prev => append ? [...prev, ...(res.data || [])] : (res.data || []));
        setTotal(res.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPage(0);
    loadProfiles(filters, 0, false);
  }, [filters, loadProfiles]);

  const handleFilterChange = (key, val) => {
    setFilters(prev => ({ ...prev, [key]: val }));
  };

  const clearFilter = (key) => setFilters(prev => ({ ...prev, [key]: '' }));

  const activeFilters = Object.entries(filters).filter(([k, v]) => v && k !== 'search');

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    loadProfiles(filters, next, true);
  };

  return (
    <div className="m-container">
      {/* Top bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="m-section-title">👥 Browse Profiles</h1>
          <p style={{ color: 'var(--m-muted)', fontSize: 14 }}>
            {loading ? 'Loading...' : `${total} profile${total !== 1 ? 's' : ''} found`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="text"
            className="m-form-control m-search-input"
            placeholder="🔍 Search by name, city, occupation…"
            style={{ flex: 1, minWidth: 180, maxWidth: 260 }}
            value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
          />
          <button
            className={`m-btn m-btn-ghost m-btn-sm${showFilters ? ' m-btn-outline' : ''}`}
            onClick={() => setShowFilters(s => !s)}
          >
            🔧 Filters {activeFilters.length > 0 && `(${activeFilters.length})`}
          </button>
        </div>
      </div>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="m-active-filters">
          {activeFilters.map(([k, v]) => (
            <span key={k} className="m-tag">
              {k}: {v}
              <span className="m-tag-remove" onClick={() => clearFilter(k)}>✕</span>
            </span>
          ))}
          <button
            className="m-btn m-btn-ghost m-btn-sm"
            onClick={() => setFilters({ gender: '', ageMin: '', ageMax: '', religion: '', state: '', education: '', search: filters.search })}
          >
            Clear all
          </button>
        </div>
      )}

      <div className={showFilters ? 'm-layout-two' : ''}>
        {/* Filter sidebar */}
        {showFilters && (
          <div className="m-filter-sidebar">
            <div className="m-filter-title">🔧 Filter Profiles</div>

            <div className="m-filter-section">
              <div className="m-filter-section-label">Gender</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['', 'bride', 'groom'].map(g => (
                  <button
                    key={g}
                    className={`m-chip${filters.gender === g ? ' active' : ''}`}
                    onClick={() => handleFilterChange('gender', g)}
                  >
                    {g === '' ? 'All' : g === 'bride' ? '👰 Bride' : '🤵 Groom'}
                  </button>
                ))}
              </div>
            </div>

            <div className="m-filter-section">
              <div className="m-filter-section-label">Age Range</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="number" className="m-form-control" placeholder="Min" min="18" max="70"
                  value={filters.ageMin} onChange={e => handleFilterChange('ageMin', e.target.value)}
                />
                <input
                  type="number" className="m-form-control" placeholder="Max" min="18" max="70"
                  value={filters.ageMax} onChange={e => handleFilterChange('ageMax', e.target.value)}
                />
              </div>
            </div>

            <div className="m-filter-section">
              <div className="m-filter-section-label">Religion</div>
              <select className="m-form-control" value={filters.religion} onChange={e => handleFilterChange('religion', e.target.value)}>
                <option value="">Any Religion</option>
                {filterOptions.religions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <div className="m-filter-section">
              <div className="m-filter-section-label">State</div>
              <select className="m-form-control" value={filters.state} onChange={e => handleFilterChange('state', e.target.value)}>
                <option value="">Any State</option>
                {filterOptions.states.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="m-filter-section">
              <div className="m-filter-section-label">Education</div>
              <select className="m-form-control" value={filters.education} onChange={e => handleFilterChange('education', e.target.value)}>
                {EDUCATIONS.map(e => (
                  <option key={e} value={e === 'Any' ? '' : e}>{e}</option>
                ))}
              </select>
            </div>

            <button
              className="m-btn m-btn-ghost m-btn-sm m-btn-full"
              onClick={() => setFilters({ gender: '', ageMin: '', ageMax: '', religion: '', state: '', education: '', search: '' })}
            >
              Reset All Filters
            </button>
          </div>
        )}

        {/* Profiles grid */}
        <div>
          {loading && profiles.length === 0 ? (
            <div className="m-profiles-grid">
              {[1,2,3,4,5,6,7,8].map(i => (
                <div key={i} className="m-profile-card">
                  <div className="m-profile-card-header">
                    <div className="m-skeleton" style={{ width: 80, height: 80, borderRadius: '50%' }} />
                    <div className="m-skeleton" style={{ width: '70%', height: 18, marginTop: 8 }} />
                    <div className="m-skeleton" style={{ width: '50%', height: 14 }} />
                  </div>
                  <div className="m-profile-card-body">
                    <div className="m-skeleton" style={{ height: 14, width: '80%' }} />
                    <div className="m-skeleton" style={{ height: 14, width: '60%' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : profiles.length === 0 ? (
            <div className="m-empty-state">
              <span className="m-empty-state-icon">🔍</span>
              <h3>No Profiles Found</h3>
              <p>Try adjusting your filters or broadening your search criteria.</p>
              <button
                className="m-btn m-btn-outline"
                onClick={() => setFilters({ gender: '', ageMin: '', ageMax: '', religion: '', state: '', education: '', search: '' })}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              <div className="m-profiles-grid">
                {profiles.map(p => <ProfileCard key={p.id} p={p} />)}
              </div>
              {profiles.length < total && (
                <div className="m-pagination">
                  <button className="m-btn m-btn-outline" onClick={loadMore} disabled={loading}>
                    {loading ? 'Loading…' : `Load More (${total - profiles.length} remaining)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
